import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, cn } from '@karozik/ui';
import { STATUS_LABEL, toLocalInput, type AdminEvent } from '../types';

export function Events() {
  const [events, setEvents] = useState<AdminEvent[] | null>(null);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    void api<AdminEvent[]>('/api/admin/events').then(setEvents);
  }, []);

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <header className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">
          <span className="text-gradient">KaroZik</span> · Soirées
        </h1>
        <button
          onClick={async () => {
            await api('/api/admin/logout', { method: 'POST' });
            navigate('/login');
          }}
          className="text-sm text-ink-dim"
        >
          Déconnexion
        </button>
      </header>

      <ul className="mt-6 space-y-3">
        {events?.map((e) => (
          <li key={e.id}>
            <Link to={`/events/${e.id}`} className="flex items-center gap-4 rounded-2xl bg-surface p-4 transition hover:bg-surface-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-lg font-semibold">{e.name}</div>
                <div className="text-sm text-ink-dim">
                  {new Date(e.startsAt).toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' })}
                </div>
                <div className="mt-1 text-xs text-ink-faint">
                  {e.guests} invités · {e.tracksPlayed} titres joués · pool de {e.poolSize}
                </div>
              </div>
              <span
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-bold',
                  e.status === 'live' ? 'bg-lime text-night' : e.status === 'upcoming' ? 'bg-cyan/20 text-cyan' : 'bg-surface-3 text-ink-dim',
                )}
              >
                {STATUS_LABEL[e.status]}
              </span>
            </Link>
          </li>
        ))}
        {events?.length === 0 && <li className="rounded-2xl bg-surface p-6 text-center text-ink-dim">Aucune soirée pour l’instant.</li>}
      </ul>

      {creating ? (
        <CreateEvent onCreated={(e) => navigate(`/events/${e.id}`)} onCancel={() => setCreating(false)} />
      ) : (
        <button onClick={() => setCreating(true)} className="bg-gradient-neon mt-6 w-full rounded-2xl py-4 font-bold">
          ＋ Nouvelle soirée
        </button>
      )}
    </main>
  );
}

function CreateEvent({ onCreated, onCancel }: { onCreated(e: AdminEvent): void; onCancel(): void }) {
  const tonight = new Date();
  tonight.setHours(19, 0, 0, 0);
  const [name, setName] = useState('Soirée de famille');
  const [startsAt, setStartsAt] = useState(toLocalInput(tonight.getTime()));
  const [endsAt, setEndsAt] = useState(toLocalInput(tonight.getTime() + 8 * 3600_000));
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="mt-6 space-y-4 rounded-2xl bg-surface p-5"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          onCreated(await api<AdminEvent>('/api/admin/events', { method: 'POST', json: { name, startsAt: new Date(startsAt), endsAt: new Date(endsAt) } }));
        } catch (err) {
          setError((err as Error).message);
        }
      }}
    >
      <Field label="Nom">
        <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Début (ouverture du QR code)">
          <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="input" />
        </Field>
        <Field label="Fin (fermeture)">
          <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="input" />
        </Field>
      </div>
      {error && <p className="text-sm text-danger">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-2xl bg-surface-2 py-3">
          Annuler
        </button>
        <button className="bg-gradient-neon flex-[2] rounded-2xl py-3 font-bold">Créer</button>
      </div>
    </form>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-ink-dim">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-faint">{hint}</span>}
    </label>
  );
}
